import type {
  GlobalConfigFragment,
  InitDataQuery,
  MenuLinkTreeElementFirstFragment,
} from '#graphql-operations'

export interface InitData {
  metaMenuLinks: MenuLinkTreeElementFirstFragment[]
  mainMenuLinks: MenuLinkTreeElementFirstFragment[]
  footerMenuLinks: MenuLinkTreeElementFirstFragment[]
  footerMetaMenuLinks: MenuLinkTreeElementFirstFragment[]
  translations: Record<string, string | [string, string]>
  globalConfig: {
    address?: GlobalConfigFragment['address']
    companyName?: GlobalConfigFragment['companyName']
    phoneNumber?: GlobalConfigFragment['phoneNumber']
    emailAddress?: GlobalConfigFragment['emailAddress']
    openPositionsPage?: GlobalConfigFragment['openPositionsPage']
    onCallServicePhone?: GlobalConfigFragment['onCallServicePhone']
    nodeOverviewLinkText?: GlobalConfigFragment['nodeOverviewLinkText']
    linkedin?: GlobalConfigFragment['linkedin']
    instagram?: GlobalConfigFragment['instagram']
    facebook?: GlobalConfigFragment['facebook']
    youtube?: GlobalConfigFragment['youtube']
    ctaDefaultLink?: GlobalConfigFragment['ctaDefaultLink']
    ctaDefaultLabel?: GlobalConfigFragment['ctaDefaultLabel']
    ctaCarreerLink?: GlobalConfigFragment['ctaCarreerLink']
    ctaCarreerLabel?: GlobalConfigFragment['ctaCarreerLabel']
    ctaApprenticeshipsLink?: GlobalConfigFragment['ctaApprenticeshipsLink']
    ctaApprenticeshipsLabel?: GlobalConfigFragment['ctaApprenticeshipsLabel']
    locationSearchOverviewPage?: GlobalConfigFragment['locationSearchOverviewPage']
    locationSearchContactPage?: GlobalConfigFragment['locationSearchContactPage']
    locationSearchMenuPage?: GlobalConfigFragment['locationSearchMenuPage']
    referencesPage?: GlobalConfigFragment['referencesPage']
  }
  competencies: [
    {
      losysIds: number[]
      id: string
      name: string
      weight: number
      parent: number[]
      children: number[]
    },
  ]
}

function getTranslations(
  v?: InitDataQuery,
): Record<string, string | [string, string]> {
  if (!v) {
    return {}
  }
  return Object.entries(
    (v.translations || {}) as Record<
      string,
      string | { singular?: string; plural?: string }
    >,
  ).reduce<Record<string, string | [string, string]>>(
    (acc, [fullKey, value]) => {
      const keyWithDots = fullKey.replace('__', '.')
      if (typeof value === 'string') {
        acc[keyWithDots] = value
      } else if (typeof value === 'object' && value.plural && value.singular) {
        acc[keyWithDots] = [value.singular, value.plural]
      }
      return acc
    },
    {},
  )
}

function filterMenuLinksRecursively(links: any[], lang: string): any[] {
  return links
    .filter((item) => {
      const translations = item.link?.translations as
        | Record<string, any>
        | undefined
      return !!translations?.[lang]
    })
    .map((item) => {
      if (item.subtree) {
        return {
          ...item,
          subtree: filterMenuLinksRecursively(item.subtree, lang),
        }
      }
      return item
    })
}

export default async function (): Promise<Ref<InitData>> {
  const currentLanguage = useCurrentLanguage()
  const data = useState<InitData>('initData')

  // Let's try to fetch the data from the cache first.
  const event = useRequestEvent()
  const { value, addToCache } = await useDataCache<InitData>(
    'initData_' + currentLanguage.value,
    event,
  )
  if (value) {
    data.value = value
    return data
  }

  // If the data is not in the cache, we fetch it from the payload / useState().
  if (data.value) {
    return data
  }

  const config = useRuntimeConfig()
  // Read the host from .env NUXT_HOST to support multisite setup.
  const url = useRequestURL()
  let host = url.hostname
  if (import.meta.server) {
    host = config.requestHost
  }

  // Fetch the data from the server.
  data.value = await useGraphqlQuery({
    name: 'initData',
    fetchOptions: {
      query: {
        language: currentLanguage.value,
        __server: 'true',
      },
      headers: {
        host,
        'x-forwarded-host': host,
      },
    },
  }).then((v) => {
    const tenantId = useTenant()

    const initData = {
      metaMenuLinks: computed(() => {
        switch (tenantId) {
          case 'kuma':
            return v.data.metaMenuKuma?.links || []
          default:
            return v.data.metaMenu?.links || []
        }
      }),
      mainMenuLinks: computed(() => {
        const menu =
          tenantId === 'kuma'
        ? v.data.mainMenuKuma?.links || []
        : v.data.mainMenu?.links || []

        return filterMenuLinksRecursively(menu, currentLanguage.value)
      }),
      footerMenuLinks: computed(() => {
        switch (tenantId) {
          case 'kuma':
            return v.data.footerMenuKuma?.links || []
          default:
            return v.data.footerMenu?.links || []
        }
      }),
      footerMetaMenuLinks: computed(() => {
        switch (tenantId) {
          case 'kuma':
            return v.data.footerMetaMenuKuma?.links || []
          default:
            return v.data.footerMetaMenu?.links || []
        }
      }),
      globalConfig: v.data[tenantId] || {},
      translations: getTranslations(v.data),
    }

    // The cache tags are coming from the onServerResponse() function in the graphqlMiddleware.
    if (
      import.meta.server &&
      v.__cacheability?.isCacheable &&
      v.__cacheability.tagsNuxt
    ) {
      addToCache(initData, v.__cacheability.tagsNuxt)
    }

    return initData
  })

  return data
}
